import React, { ReactNode, useMemo } from 'react'
import i18n from 'constants/strings'
import styles from './styles/profile-header.module.scss'
import { Player } from 'types/global'
import InfoBoxContainer from 'components/generic/InfoBoxContainer'
import { useCountries } from 'hooks/use-countries'
import { getUnknownDisplayText, initializeHeightValues } from 'utils'
import classNames from 'classnames'
import BtnSecondary from 'components/generic/BtnSecondary'
import { MY_ACCOUNT_FULL_URL } from 'constants/global'

interface AdditionalInfoProps {
  profile: Player
  isOwnProfile: boolean
}

const AdditionalInfo: React.FC<AdditionalInfoProps> = ({ profile, isOwnProfile }) => {
  const { getCountryName } = useCountries()

  const additionalInfo: { title: string; get: (p: Player) => ReactNode | undefined }[] = useMemo(
    () => [
      {
        title: i18n.t('strProfileHeader.residence'),
        get: p => p.residence && getCountryName(p.residence),
      },
      {
        title: i18n.t('strProfileHeader.height'),
        get: p => {
          let heightText: ReactNode | undefined
          if (p.height) {
            const { m, cm, ft, inches } = initializeHeightValues(p.height)
            heightText = (
              <>
                {`${ft}' ${inches}"`}{' '}
                <span>{`(${m === '0' ? '' : m}${cm.length < 2 ? '0' + cm : cm}cm)`}</span>
              </>
            )
          }

          return heightText
        },
      },
      {
        title: i18n.t('strProfileHeader.preferredHand'),
        get: p =>
          p.preferredHand && p.preferredHand !== getUnknownDisplayText()
            ? p.preferredHand
            : undefined,
      },
      {
        title: i18n.t('strProfileHeader.backhand'),
        get: p => (p.backhand && p.backhand !== getUnknownDisplayText() ? p.backhand : undefined),
      },
      { title: i18n.t('strProfileHeader.favouriteShot'), get: p => p.favouriteShot },
    ],
    [getCountryName]
  )

  return (
    <InfoBoxContainer>
      <h2>{i18n.t('strProfileHeader.additionalInfo')}</h2>
      <div className={styles.additionalInfoWrapper}>
        <div className={styles.additionalInfoContainer}>
          {additionalInfo.map(({ title, get }) => {
            const info = get(profile)
            return (
              <div className={styles.additionalInfo} key={title}>
                <div className={styles.additionalInfoInner}>
                  <h4>{title}</h4>
                  <p className={classNames({ [styles.noInfo]: !info })}>{info || <span>-</span>}</p>
                </div>
              </div>
            )
          })}
        </div>
        {isOwnProfile && (
          <div className={styles.btnContainer}>
            <BtnSecondary
              className={styles.updateBtn}
              size='medium'
              onClick={() => (window.location.href = MY_ACCOUNT_FULL_URL)}
            >
              {i18n.t('strCommonUI.button.updateInfo')}
            </BtnSecondary>
          </div>
        )}
      </div>
    </InfoBoxContainer>
  )
}

export default AdditionalInfo
